// src/components/Admin/ReportManager/ReportLogTable.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
    Paper, Box, CircularProgress, TextField,
    FormControl, InputLabel, Select, MenuItem,
    TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Pagination,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../../api/axios';

const ACTION_LABEL = {
    pending: '대기',
    blinded: '블라인드',
    deleted: '삭제',
    restored: '복구',
    ignored: '무시',
};
const REPORT_TYPE_LABEL = {
    post: '게시글',
    comment: '댓글',
    user: '회원',
};
const SORT_OPTIONS = [
    { value: 'date_desc', label: '최신순' },
    { value: 'date_asc', label: '과거순' },
    { value: 'action', label: '처리종류순' },
    { value: 'report', label: '신고ID순' },
];
const ROWS_PER_PAGE = 15;

export default function ReportLogTable() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    /* 검색·필터·정렬 상태 */
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date_desc');
    const [page, setPage] = useState(1);

    /* 데이터 로드 */
    useEffect(() => {
        api.get('/api/reports/logs/all/')
            .then((res) => {
                const arr = res.data.logs || [];
                setLogs(arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
            })
            .catch(() => alert('로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* 필터·정렬 */
    const processed = useMemo(() => {
        let arr = logs;

        if (search.trim()) {
            const q = search.toLowerCase();
            arr = arr.filter((log) =>
                String(log.report_id).includes(q)
                || (REPORT_TYPE_LABEL[log.report_type] || log.report_type).toLowerCase().includes(q)
                || (log.actor || '').toLowerCase().includes(q));
        }
        if (actionFilter !== 'all') arr = arr.filter((log) => log.action === actionFilter);

        return [...arr].sort((a, b) => {
            if (sortBy === 'date_desc') return new Date(b.created_at) - new Date(a.created_at);
            if (sortBy === 'date_asc') return new Date(a.created_at) - new Date(b.created_at);
            if (sortBy === 'action') return (a.action || '').localeCompare(b.action || '');
            if (sortBy === 'report') return a.report_id - b.report_id;
            return 0;
        });
    }, [logs, search, actionFilter, sortBy]);

    /* 페이지 */
    useEffect(() => setPage(1), [search, actionFilter, sortBy, logs]);
    const totalPages = Math.max(1, Math.ceil(processed.length / ROWS_PER_PAGE));
    const paginated = useMemo(() => {
        const start = (page - 1) * ROWS_PER_PAGE;
        return processed.slice(start, start + ROWS_PER_PAGE);
    }, [processed, page]);

    if (loading) {
        return <CircularProgress sx={{ display: 'block', mt: 4, mx: 'auto' }} />;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                /* 모바일: 전체 90 % 축소 */
                '@media (max-width:600px)': {
                    transform: 'scale(0.9)',
                    transformOrigin: 'top left',
                    p: 1.5,
                },
            }}
        >
            {/* 검색·필터·정렬 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    label="ID/대상/처리자"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 250 }}
                />

                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>처리 종류</InputLabel>
                    <Select
                        value={actionFilter}
                        label="처리 종류"
                        onChange={(e) => setActionFilter(e.target.value)}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        {Object.entries(ACTION_LABEL).map(([k, v]) => (
                            <MenuItem key={k} value={k}>{v}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>정렬 기준</InputLabel>
                    <Select
                        value={sortBy}
                        label="정렬 기준"
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        {SORT_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* 테이블 */}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    '& tr:hover': { bgcolor: 'grey.100' },
                    '& td, & th': {
                        borderBottom: '1px solid #eee',
                        textAlign: 'center',
                        p: 1.25,
                        whiteSpace: 'nowrap',
                        '@media (max-width:600px)': {
                            p: 0.75,
                            fontSize: '0.8rem',
                        },
                    },
                    '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                    /* 모바일 가로 스크롤 */
                    '@media (max-width:600px)': {
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>신고&nbsp;ID</TableCell>
                            <TableCell>대상</TableCell>
                            <TableCell>처리자</TableCell>
                            <TableCell>처리</TableCell>
                            <TableCell>처리일</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((log, idx) => (
                                <TableRow key={log.id ?? `${log.report_id}-${idx}`}>
                                    <TableCell>{log.report_id}</TableCell>
                                    <TableCell>{REPORT_TYPE_LABEL[log.report_type] || log.report_type}</TableCell>
                                    <TableCell>{log.actor || '알 수 없음'}</TableCell>
                                    <TableCell>{ACTION_LABEL[log.action] || log.action}</TableCell>
                                    <TableCell>{dayjs(log.created_at).format('YYYY-MM-DD HH:mm')}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 2,
                        '@media (max-width:600px)': { transform: 'scale(0.9)' },
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        showFirstButton
                        showLastButton
                        shape="rounded"
                        color="primary"
                    />
                </Box>
            )}
        </Paper>
    );
}
